import clsx from 'clsx';
import React from 'react';
import { InView } from 'react-intersection-observer';

import IconArrow from '@components/Images/IconArrow';
import useAwarnessBarVisibility from '@hooks/useAwarnessBarVisibility';
import useElementsHeight from '@hooks/useElementsHeight';
import useEventTracking from '@hooks/useEventTracking';
import useWindowSize from '@hooks/useWindowSize';
import { deep_research_path } from '@routes';
import { ClickTarget, ImpressionElement, Page, click, impression } from '@tracking';

interface AITopBannerProps {
  currentPage: Page;
  isContentPaywalled: boolean;
  isNonProSubscriber: boolean;
  shouldShowScrolledNav: boolean;
}

const UPGRADE_TEXT = 'Upgrade to ask';
const ASK_TEXT = 'Ask';

const AITopBanner = ({
  currentPage,
  isContentPaywalled,
  isNonProSubscriber,
  shouldShowScrolledNav,
}: AITopBannerProps) => {
  useAwarnessBarVisibility(shouldShowScrolledNav);
  const { trackEvent, Track } = useEventTracking({
    location: currentPage,
  });
  const { width: windowWidth } = useWindowSize();

  useElementsHeight({
    selectors: [
      '.top-sticky-banner',
      windowWidth <= 992 && windowWidth > 767 ? '#global-nav-wrapper' : '#global-nav',
    ].filter(Boolean),
    isScrollStyle: shouldShowScrolledNav,
    dependency: [windowWidth],
    cssVariable: 'ai-banner-height',
  });

  const ctaText = isNonProSubscriber ? UPGRADE_TEXT : ASK_TEXT;
  const shouldShowHoverEffect =
    [Page.Article, Page.Briefing, Page.Project].includes(currentPage) && isContentPaywalled;

  return (
    <Track>
      <InView
        triggerOnce={true}
        onChange={(inView) => {
          inView &&
            trackEvent(impression({ impressionElement: ImpressionElement.DeepResearchBanner }));
        }}
      >
        <aside
          id="ai-research-banner"
          className={clsx(
            'group relative h-full overflow-hidden sm:!top-[calc(var(--ai-banner-height,0px))] sm:border-none sm:px-28 sm:py-0',
            {
              ' !top-[calc(var(--ai-banner-height,0px)+24px)] z-[-1] bg-black':
                shouldShowHoverEffect,
              '!top-0': currentPage === Page.Homepage,
            }
          )}
        >
          {shouldShowHoverEffect && (
            <div className="absolute inset-0 origin-bottom scale-y-0 bg-grayscale-beige transition-transform duration-300 ease-out group-hover:scale-y-100" />
          )}
          <a
            href={deep_research_path()}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickTarget: ClickTarget.DeepResearchBanner,
                  clickText: ctaText,
                })
              )
            }
            className={clsx(
              'group relative mx-auto flex h-full max-w-container-lg items-center justify-center overflow-hidden bg-black py-6 pr-24 text-white sm:h-max sm:pr-0'
            )}
            data-testid="ai-research-banner"
          >
            <div className="absolute inset-0 origin-bottom scale-y-0 bg-grayscale-beige transition-transform duration-300 ease-out group-hover:scale-y-100" />
            <h3 className="z-9 relative m-0 self-end text-center font-suisse-intl text-overline-md font-normal text-white transition-colors duration-300 group-hover:text-black sm:text-button-md">
              <span className="inline-block">In-depth insights in seconds. Ask Deep Research.</span>
            </h3>
            <IconArrow className="z-9 relative ml-12 h-16 rotate-180 text-primary-magenta transition-transform duration-200 group-hover:rotate-[180deg] sm:rotate-[220deg]" />
          </a>
        </aside>
      </InView>
    </Track>
  );
};

export default AITopBanner;
